import { computedFrom, useView } from "aurelia-framework";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { CwChapterClientModel, CwsaStatusEnum, GetCwSelfAssessmentReviewResponse } from "../../../../../../models/cwsa/cwsa.models";
import { CwSelfAssessmentStatusHistoryClientModel } from "../../../../../../models/cwsa/cwsa.models";
import * as moment from "moment";
import { CorrespondenceCountsResponse } from "../../../../../../models/vessels/vessel.models";
import { EventAggregator } from "aurelia-event-aggregator";
import { RightshipQiMessages } from "../../../../../../configuration/rightshipqi-event-messages.constants";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { CwsaAssignment } from "../../components/assignment/cwsa-assignment";
import { UserClientService } from "@rightship-tech/common-ui/services/basic/user-client.service";
import Enumerable from "linq";

@useView('views/pages/modules/cwsa/tabs/form-data/cwsa-form-data.html')
export class CwsaFormData {

    public cwsaStatuses: CwSelfAssessmentStatusHistoryClientModel[];
    public isShowMoreSectionStatus: boolean;
    public cwSelfAssessment: GetCwSelfAssessmentReviewResponse;
    public cwSelfAssessmentChapters: CwChapterClientModel[];
    public formLoading: boolean;
    public reassignment: CwsaStatusEnum = CwsaStatusEnum.Reassignment;
    correspondenceCounts: CorrespondenceCountsResponse;
    cwSelfAssessmentChaptersRfiCounts: { [chapterId: number]: number } = {};
    cwsaAssignment: CwsaAssignment;
    selectedCategories: string[] = [];
    selectedChapterIndex: number = 0;
    yes: string = 'yes';
    no: string = 'no';
    private reload: () => void;

    @computedFrom('cwSelfAssessmentChapters', 'selectedChapterIndex')
    public get yesCount() {
        return Enumerable.from(this.cwSelfAssessmentChapters[this.selectedChapterIndex].sections)
            .selectMany(section => section.questions)
            .selectMany(question => question.choices)
            .count(choice => choice.isSelected && choice.name.toLowerCase() === this.yes);
    }

    @computedFrom('cwSelfAssessmentChapters', 'selectedChapterIndex')
    public get noCount() {
        return Enumerable.from(this.cwSelfAssessmentChapters[this.selectedChapterIndex].sections)
            .selectMany(section => section.questions)
            .selectMany(question => question.choices)
            .count(choice => choice.isSelected && choice.name.toLowerCase() === this.no);
    }

    @computedFrom('selectedCategories', 'selectedCategories.length')
    public get yesSelected() {
        return this.selectedCategories.indexOf(this.yes) >= 0;
    }

    @computedFrom('selectedCategories', 'selectedCategories.length')
    public get noSelected() {
        return this.selectedCategories.indexOf(this.no) >= 0;
    }

    @computedFrom('cwSelfAssessmentChapters', 'selectedChapterIndex', 'selectedCategories', 'selectedCategories.length')
    public get selectedChapter() {
        const result = this.cwSelfAssessmentChapters && this.cwSelfAssessmentChapters.length > 0 
            ? JSON.parse(JSON.stringify(this.cwSelfAssessmentChapters[this.selectedChapterIndex])) as CwChapterClientModel 
            : null;

        if (this.selectedCategories && this.selectedCategories.length > 0) {
            result.sections.forEach(section => {
                section.questions = Enumerable.from(section.questions)
                    .where(question => question.choices.some(choice => choice.isSelected && this.selectedCategories.includes(choice.name.toLowerCase())))
                    .toArray();
            });
        }

        return result;
    }

    constructor(private dialogClientService: DialogClientService,
        private eventAggregator: EventAggregator,
        private userClientService: UserClientService
    ) { }

    activate(args: {
        cwSelfAssessment: GetCwSelfAssessmentReviewResponse,
        correspondenceCounts: CorrespondenceCountsResponse,
        cwsaAssignment: CwsaAssignment,
        reload: () => void
    }) {
        this.cwSelfAssessmentChaptersRfiCounts = {};
        this.cwSelfAssessment = args.cwSelfAssessment;
        this.correspondenceCounts = args.correspondenceCounts;

        if (this.cwSelfAssessment.statusHistory.length > 0) {
            // don't show the first status (in progress) since it doesn't make sense to show it
            // https://rightship.atlassian.net/browse/DEL-17281
            this.cwsaStatuses = this.cwSelfAssessment.statusHistory.slice(0, this.cwSelfAssessment.statusHistory.length - 1);
        }

        this.cwSelfAssessmentChapters = this.cwSelfAssessment.chapters;

        this.correspondenceCounts.countByLinkedEntities.forEach(item => {
            if (item.linkedEntityTypeId == EntityTypes.CrewWelfareChapter) {
                if (!this.cwSelfAssessmentChaptersRfiCounts[item.linkedEntityId]) {
                    this.cwSelfAssessmentChaptersRfiCounts[item.linkedEntityId] = 0;
                }
                this.cwSelfAssessmentChaptersRfiCounts[item.linkedEntityId] += 1;
            }
        });

        this.cwsaAssignment = args.cwsaAssignment;
        this.reload = args.reload;
    }

    openAddRfiDialog(cwSelfAssessmentChapterId: number) {
        if (this.cwsaAssignment.assignedEntity.assignedTo != this.userClientService.currentUser.user.id) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CwsaAssignmentDialog, {
                name: "Cwsa assessment",
                submit: this.cwsaAssignment.assignToMe,
                callback: this.openAddRfiDialog.bind(this, cwSelfAssessmentChapterId),
                currentAssignment: null
            }, true, false, false);
        }
        else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddSubtaskDialog, {
                entityId: this.cwSelfAssessment.cwSelfAssessmentId,
                entityTypeId: EntityTypes.CrewWelfare,
                emails: [],
                linkedEntityId: cwSelfAssessmentChapterId,
                linkedEntityTypeId: EntityTypes.CrewWelfareChapter,
                isWet: false,
                liaseWithShipManagers: [],
                isPartialRFI: false,
                subtaskTypes: [],
                skipInvitation: true,
                reload: this.reload
            }, true, false, false, 'right');
        }
    }

    goToCorrespondenceTab(cwSelfAssessmentChapterId: number) {
        this.eventAggregator.publish(RightshipQiMessages.viewCorrespondenceTab, {
            initialSubtaskLinkedEntityId: cwSelfAssessmentChapterId,
            initialSubtaskLinkedEntityTypeId: EntityTypes.CrewWelfareChapter
        });
    }

    selectChapter(chapterIndex: number) {
        this.selectedChapterIndex = chapterIndex - 1;
    }

    //Show more section status
    showMoreSectionStatus() {
        this.isShowMoreSectionStatus = !this.isShowMoreSectionStatus;
    }

    //Format date
    getFormattedDate(date: string) {
        return moment(date).format('DD MMM YYYY, HH:mm');
    }

    chapterHasFailedCriteria(chapter: CwChapterClientModel) {
        const failedCriteriaList = [];

        //Check if the chapter has any failed criteria and push them to the list if they do
        chapter.sections.forEach(section => {
            section.questions.forEach(question => {
                if (!question.isMlcRequirementSatisfied) {
                    failedCriteriaList.push(question);
                }
            });
        });

        if (failedCriteriaList.length > 0) {
            return true;
        }
    }

    questionAdditionalContext(question) {

        if (!question || !question.additionalContextTemplate) {
            return '';
        }

        const additionalContext = question.additionalContext ? JSON.parse(question.additionalContext) : {};
        let index = 0;

        const html = '<div>' + question.additionalContextTemplate.replace(/<textbox>/g, () => {

            const textValue = `text${++index}`;
            const propertyValue = (additionalContext[textValue]?.trim() !== '') ? additionalContext[textValue] : 'N/A';
            return `<b>${propertyValue}</b>`;
        }) + '</div>';
        return html;
    }

    getStatusName(status: CwSelfAssessmentStatusHistoryClientModel) {
        return status.statusId === CwsaStatusEnum.AwaitingResponse ? 'Waiting for Ship Manager’s Response' : status.status;
    }

    filter(passed: string) {
        if (this.selectedCategories.indexOf(passed) < 0) {
            this.selectedCategories.push(passed);
        } else {
            this.selectedCategories = this.selectedCategories.filter(x => x !== passed);
        }
    }

    clearFilters() {
        this.selectedCategories = [];
    }
}